import { useState } from 'react';
import { CURRENCIES, CATEGORIES, INCOME_CATEGORIES, IRS_CATEGORIES, IRS_INCOME_CATEGORIES, INCOME_CATEGORY_TAX_MAP, IRS_MILEAGE_RATE, BIZ_CLASS, CLASS_SELECT_KEYS, CATEGORY_KEY_BY_LABEL, INCOME_CAT_KEY_BY_LABEL, EXPENSE_REFUND_CLASS_KEYS, resolveClassification } from '../utils/constants';
import { makeKey } from '../utils/categoryHelpers';
import { formatAmount } from '../utils/formatters';
import { Receipt, CreditCard, Landmark } from 'lucide-react';

const REPORT_TYPE_META = {
  expense:     { label: 'Expense Report', icon: Receipt,    color: '#0A7C8A' },
  credit_card: { label: 'Credit Card',    icon: CreditCard, color: '#7C3AED' },
  bank:        { label: 'Bank Statement', icon: Landmark,   color: '#1D4ED8' },
};

function isPdf(expense) {
  if (expense.mimeType === 'application/pdf') return true;
  if (expense.imagePreview?.startsWith('data:application/pdf')) return true;
  return false;
}
function openInNewTab(e, url) {
  e.stopPropagation();
  if (!url) return;
  // Convert base64 data URLs to blob URLs so the browser opens them properly
  if (url.startsWith('data:')) {
    const [header, data] = url.split(',');
    const mime = header.match(/:(.*?);/)[1];
    const bytes = atob(data);
    const ab = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) ab[i] = bytes.charCodeAt(i);
    const blob = new Blob([ab], { type: mime });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  } else {
    window.open(url, '_blank');
  }
}
function ReceiptThumbnail({ expense }) {
  const [imgError, setImgError] = useState(false);
  const preview = expense.imagePreview;

  if (!preview) return null;

  const pdf = isPdf(expense) || imgError;

  if (pdf) {
    return (
      <button
        onClick={(e) => openInNewTab(e, preview)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 6,
          padding: '6px 10px',
          borderRadius: 8,
          border: '1.5px solid #E5E7EB',
          background: '#F9FAFB',
          cursor: 'pointer',
          color: '#0A7C8A',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {isPdf(expense) ? 'View PDF' : 'View Receipt'}
      </button>
    );
  }

  return (
    <div style={{ marginBottom: 6 }}>
      <img
        src={preview}
        alt="Receipt"
        onError={() => setImgError(true)}
        onClick={(e) => openInNewTab(e, preview)}
        style={{
          width: '100%',
          height: 300,
          objectFit: 'cover',
          borderRadius: 8,
          border: '1px solid #E5E7EB',
          cursor: 'zoom-in',
          display: 'block',
        }}
      />
    </div>
  );
}


const INCOME_CLASS_KEYS = new Set(['business_income', 'personal_income', 'unclassified_credit']);

function isIncomeClass(cls) {
  return INCOME_CLASS_KEYS.has(cls);
}
function isRefundClass(cls) {
  return EXPENSE_REFUND_CLASS_KEYS.has(cls);
}

// Normalize legacy data (personal → personal_expense, isRefund → refund classification, etc.)
// Also strips fields that no longer have meaning after the migration.
function normalizeExpense(e) {
  if (!e) return e;
  const resolved = resolveClassification(e.businessClassification, e.isRefund, e.transactionDirection);
  const out = { ...e };
  if (resolved !== e.businessClassification) out.businessClassification = resolved;
  if (e.isRefund) {
    delete out.isRefund;
    if (e.incomeTaxType === 'Refund / Reimbursement (non-taxable)') delete out.incomeTaxType;
  }
  return out;
}

function EditModal({ expense, onSave, onClose, categories, incomeCategories, addCategory, addIncomeCategory,
                     savedFiles, currentFileName, reportType, onCopyExpense, onMoveExpense, readOnly, lockedFields = [] }) {
  const isLocked = (field) => lockedFields.includes(field);
  const [form, setForm] = useState(() => normalizeExpense(expense));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Classification drives income vs expense — fall back to transactionDirection if unset
  const cls = form.businessClassification;
  const isIncome = cls
    ? isIncomeClass(cls)
    : expense?.transactionDirection === 'credit';
  const isRefund = isRefundClass(cls);

  const allExpenseCategories = categories || CATEGORIES;
  const allIncomeCats = incomeCategories || INCOME_CATEGORIES;
  // Income classifications use income categories; everything else (including refund classifications) uses expense categories.
  const allCategories = isIncome ? allIncomeCats : allExpenseCategories;
  const canAddCategory = !readOnly && !!(isIncome ? addIncomeCategory : addCategory);

  const toKey = (label) => CATEGORY_KEY_BY_LABEL[label] ?? INCOME_CAT_KEY_BY_LABEL[label] ?? makeKey(label);

  const handleClassificationChange = (newClass) => {
    const newIsIncome = isIncomeClass(newClass);
    const newIsRefund = isRefundClass(newClass);
    setForm(f => {
      const curIsIncome = f.businessClassification ? isIncomeClass(f.businessClassification) : expense?.transactionDirection === 'credit';
      const curIsRefund = isRefundClass(f.businessClassification);
      const update = { ...f, businessClassification: newClass || undefined };
      const incomeSideChanged = newIsIncome !== curIsIncome;
      const refundSideChanged = newIsRefund !== curIsRefund;
      if (incomeSideChanged || refundSideChanged) {
        const validCats = newIsIncome ? allIncomeCats : allExpenseCategories;
        const validKeys = validCats.map(toKey);
        if (!validKeys.includes(f.category)) update.category = '';
      }
      if (!newIsIncome && f.incomeTaxType) update.incomeTaxType = undefined;
      return update;
    });
  };

  const handleCategoryChange = (value) => {
    setForm(f => ({
      ...f,
      category: value,
      ...(value === 'mileage' && !f.mileageRate ? { mileageRate: IRS_MILEAGE_RATE } : {}),
    }));
  };

  const setMileageField = (field, value) => {
    setForm(f => {
      const updated = { ...f, [field]: value };
      const miles = parseFloat(updated.miles) || 0;
      const rate  = parseFloat(updated.mileageRate) || 0;
      if (miles > 0 && rate > 0) updated.total = (miles * rate).toFixed(2);
      return updated;
    });
  };

  const setComponent = (field, value) => {
    setForm(f => {
      const updated = { ...f, [field]: value };
      const sum =
        (parseFloat(updated.subtotal) || 0) +
        (parseFloat(updated.tax)      || 0) +
        (parseFloat(updated.tip)      || 0);
      if (sum > 0) updated.total = sum.toFixed(2);
      return updated;
    });
  };

  const componentSum =
    (parseFloat(form.subtotal) || 0) +
    (parseFloat(form.tax)      || 0) +
    (parseFloat(form.tip)      || 0);
  const totalVal = parseFloat(form.total) || 0;
  const hasMismatch = !readOnly && componentSum > 0 && totalVal > 0 && Math.abs(totalVal - componentSum) > 0.01;

  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatTax, setNewCatTax] = useState(isIncome ? "Other Income" : "Uncategorized");

  // Copy / Move state
  const otherFiles = (savedFiles || []).filter(f => f.name !== currentFileName && (f.reportType === 'expense' || !f.reportType));
  const canTransfer = !readOnly && otherFiles.length > 0 && (onCopyExpense || onMoveExpense);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  const [transferring, setTransferring] = useState(null); // 'copy' | 'move' | null
  const [transferDone, setTransferDone] = useState(null); // 'copy' | 'move' | null

  const handleTransfer = async (mode) => {
    const target = transferTarget || otherFiles[0]?.name;
    if (!target) return;
    setTransferring(mode);
    try {
      if (mode === 'copy') await onCopyExpense(expense, target);
      else await onMoveExpense(expense, target);
      setTransferDone(mode);
      setTimeout(() => {
        setTransferDone(null);
        if (mode === 'move') onClose();
      }, 1200);
    } finally {
      setTransferring(null);
    }
  };

  const handleAddCategory = () => {
    const trimmed = newCatName.trim();
    if (!trimmed) return;
    if (isIncome) {
      if (addIncomeCategory) addIncomeCategory(trimmed, newCatTax);
    } else {
      if (addCategory) addCategory(trimmed, newCatTax);
    }
    set("category", makeKey(trimmed));
    setNewCatName("");
    setNewCatTax(isIncome ? "Other Income" : "Uncategorized");
    setShowNewCat(false);
  };

  const lockedStyle = { background: "#F9FAFB", color: "#6B7280", cursor: "default" };
  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1.5px solid #E5E7EB",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    transition: "border-color 0.2s",
    colorScheme: "light",
    ...(readOnly ? lockedStyle : {}),
  };
  const lockedInput = (field) => isLocked(field) ? { ...inputStyle, ...lockedStyle } : inputStyle;

  const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: "#6B7280",
    letterSpacing: 0.8,
    marginBottom: 4,
    display: "block",
    textTransform: "uppercase",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "20px 20px 0 0",
          padding: "20px",
          width: "100%",
          maxWidth: 390,
          maxHeight: "90vh",
          overflowY: "auto",
          animation: "slideUp 0.3s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#111" }}>
                {readOnly
                  ? (expense?.transactionDirection === 'credit' ? "View Income" : "View Expense")
                  : (expense?.transactionDirection === 'credit' ? "Edit Income" : "Edit Transaction")}
              </h2>
              {(() => {
                const meta = REPORT_TYPE_META[reportType];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: meta.color + '18', borderRadius: 6, padding: '3px 8px' }}>
                    <Icon size={11} color={meta.color} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: meta.color }}>{meta.label}</span>
                  </div>
                );
              })()}
            </div>
            {readOnly && (
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>View only — changes cannot be saved</div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
              color: "#888",
              padding: 0,
              width: 30,
              height: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        {form.imagePreview && (
          <div style={{ marginBottom: 20 }}>
            <ReceiptThumbnail expense={expense} />
            {/* <img
              src={form.imagePreview}
              alt="Receipt"
              style={{
                width: "100%",
                height: 200,
                objectFit: "cover",
                borderRadius: 12,
                border: "1px solid #E5E7EB",
              }}
            /> */}
          </div>
        )}

        {/* Conversion summary — read only */}
        {expense.convertedTotal != null && (
          <div style={{
            background: expense.convertedCurrency !== expense.currency ? "#F0F4FF" : "#F9FAFB",
            borderRadius: 12,
            padding: "12px 14px",
            marginBottom: 16,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>Original</span>
              <span style={{ fontSize: 15, fontWeight: 800, color: "#111" }}>
                {formatAmount(expense.total, expense.currency)}
              </span>
            </div>
            {expense.convertedCurrency !== expense.currency && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>Report amount</span>
                  <span style={{ fontSize: 15, fontWeight: 800, color: "#0A7C8A" }}>
                    {formatAmount(expense.convertedTotal, expense.convertedCurrency)}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "#9CA3AF" }}>Exchange rate</span>
                  <span style={{ fontSize: 11, color: "#9CA3AF" }}>
                    1 {expense.currency} = {parseFloat(expense.conversionRate).toFixed(6)} {expense.convertedCurrency} + {(parseFloat(expense.adjustmentFactor) || 0)}%
                  </span>
                </div>
                {expense.conversionDate && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>Converted on</span>
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>
                      {expense.conversionDate.slice(0, 10)}
                    </span>
                  </div>
                )}
                {!readOnly && (
                  <div style={{ fontSize: 10, color: "#C4B5FD", marginTop: 2 }}>
                    Conversion is recalculated on save.
                  </div>
                )}
              </>
              
            )}

          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={labelStyle}>Vendor</label>
            <input
              style={lockedInput('vendor')}
              value={form.vendor || ""}
              onChange={(e) => set("vendor", e.target.value)}
              placeholder="e.g. Starbucks"
              disabled={readOnly || isLocked('vendor')}
            />
          </div>

          <div>
            <label style={labelStyle}>Location</label>
            <input
              style={inputStyle}
              value={form.location || ""}
              onChange={(e) => set("location", e.target.value)}
              placeholder="e.g. San Diego, CA"
              disabled={readOnly}
            />
          </div>


          {/* Business classification */}
          {(() => {
            const cls = form.businessClassification;
            const effectiveVal = cls === 'unclassified' ? 'unclassified_debit' : (cls || '');
            const c = cls ? (BIZ_CLASS[cls] || BIZ_CLASS.unclassified_debit) : null;
            return (
              <div>
                <label style={labelStyle}>Classification</label>
                <select
                  style={{ ...inputStyle, ...(c ? { borderColor: c.color, background: c.bg, color: c.color, fontWeight: 700 } : {}) }}
                  value={effectiveVal}
                  onChange={e => handleClassificationChange(e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">— please set classification —</option>
                  {CLASS_SELECT_KEYS.map(k => (
                    <option key={k} value={k}>{BIZ_CLASS[k].label}</option>
                  ))}
                </select>
              </div>
            );
          })()}

          {/* Refund hint — shown when a refund classification is picked */}
          {isRefund && (
            <div style={{ fontSize: 12, color: '#15803d', background: '#F0FDF4', borderRadius: 6, padding: '6px 10px' }}>
              Pick the expense category this refunded. The amount will net against that category.
            </div>
          )}

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Category</label>
              {canAddCategory ? (
                <button
                  type="button"
                  onClick={() => setShowNewCat(v => !v)}
                  style={linkBtnStyle}
                >
                  {showNewCat ? "Cancel" : "+ New category"}
                </button>
              ) : (
                <span style={{ fontSize: 11, color: "#9ca3af" }}>Sign in to create categories</span>
              )}
            </div>

            {showNewCat ? (
              <div style={newCatBoxStyle}>
                <input
                  style={{ ...inputStyle, marginBottom: 8 }}
                  placeholder="Category name"
                  value={newCatName}
                  onChange={e => setNewCatName(e.target.value)}
                  autoFocus
                />
                <label style={labelStyle}>{isIncome ? 'IRS Tax Type' : 'IRS Tax Category'}</label>
                <select
                  style={{ ...inputStyle, marginBottom: 8 }}
                  value={newCatTax}
                  onChange={e => setNewCatTax(e.target.value)}
                >
                  {(isIncome ? IRS_INCOME_CATEGORIES : IRS_CATEGORIES).map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <button type="button" onClick={handleAddCategory} style={addBtnStyle}>
                  Add Category
                </button>
              </div>
            ) : (
              <select
                style={inputStyle}
                value={form.category || ''}
                onChange={(e) => handleCategoryChange(e.target.value)}
                disabled={readOnly}
              >
                <option value="">— select category —</option>
                {allCategories.map((cat) => {
                  const key = toKey(cat);
                  return <option key={key} value={key}>{cat}</option>;
                })}
              </select>
            )}
          </div>

          {/* Tax type for income items only */}
          {isIncome && (
            <div>
              <label style={labelStyle}>Tax Type</label>
              <select
                style={{ ...inputStyle, borderColor: "#6ee7b7" }}
                value={form.incomeTaxType || INCOME_CATEGORY_TAX_MAP[form.category] || INCOME_CATEGORY_TAX_MAP[INCOME_CAT_KEY_BY_LABEL[form.category]] || IRS_INCOME_CATEGORIES[IRS_INCOME_CATEGORIES.length - 1]}
                onChange={(e) => set("incomeTaxType", e.target.value)}
                disabled={readOnly}
              >
                {IRS_INCOME_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                IRS tax form this income will be reported on.
              </div>
            </div>
          )}

          {/* Tax category override for "Other" expenses */}
          {!isIncome && form.category === 'other' && (
            <div>
              <label style={labelStyle}>IRS Tax Category</label>
              <select
                style={{ ...inputStyle, borderColor: "#a78bfa" }}
                value={form.taxCategory || "Uncategorized"}
                onChange={(e) => set("taxCategory", e.target.value)}
                disabled={readOnly}
              >
                {IRS_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                Override the IRS deduction category for this specific expense.
              </div>
            </div>
          )}

          {/* Mileage fields */}
          {!isIncome && form.category === 'mileage' && (
            <div style={{ background: "#EFF6FF", border: "1.5px solid #93C5FD", borderRadius: 12, padding: "14px 14px 10px" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#2563EB", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>
                Mileage Details
              </div>
              <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                <div style={{ flex: 2 }}>
                  <label style={labelStyle}>Miles</label>
                  <input
                    style={inputStyle}
                    type="number"
                    step="0.1"
                    value={form.miles || ""}
                    onChange={(e) => setMileageField("miles", e.target.value)}
                    placeholder="0.0"
                    disabled={readOnly}
                  />
                </div>
                <div style={{ flex: 2 }}>
                  <label style={labelStyle}>Rate / mi ($)</label>
                  <input
                    style={inputStyle}
                    type="number"
                    step="0.001"
                    value={form.mileageRate || ""}
                    onChange={(e) => setMileageField("mileageRate", e.target.value)}
                    placeholder={IRS_MILEAGE_RATE}
                    disabled={readOnly}
                  />
                </div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={labelStyle}>Origin</label>
                <input
                  style={{ ...inputStyle, marginBottom: 8 }}
                  value={form.mileageOrigin || ""}
                  onChange={(e) => set("mileageOrigin", e.target.value)}
                  placeholder="e.g. Home office"
                  disabled={readOnly}
                />
                <label style={labelStyle}>Destination</label>
                <input
                  style={{ ...inputStyle, marginBottom: 8 }}
                  value={form.mileageDest || ""}
                  onChange={(e) => set("mileageDest", e.target.value)}
                  placeholder="e.g. Client site"
                  disabled={readOnly}
                />
                <label style={labelStyle}>Business Purpose</label>
                <input
                  style={inputStyle}
                  value={form.mileagePurpose || ""}
                  onChange={(e) => set("mileagePurpose", e.target.value)}
                  placeholder="e.g. Client meeting"
                  disabled={readOnly}
                />
              </div>
              <div style={{ fontSize: 11, color: "#2563EB", fontWeight: 600 }}>
                {(parseFloat(form.miles) || 0).toFixed(1)} mi × ${(parseFloat(form.mileageRate) || IRS_MILEAGE_RATE).toFixed(3)}/mi = ${((parseFloat(form.miles) || 0) * (parseFloat(form.mileageRate) || IRS_MILEAGE_RATE)).toFixed(2)}
              </div>
            </div>
          )}

          {/* Food & Dining extra fields */}
          {!isIncome && form.category === 'food_dining' && (
            <div style={{ background: "#F0FDF9", border: "1.5px solid #99F6E4", borderRadius: 12, padding: "14px 14px 10px" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#0A7C8A", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>
                Dining Details
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Meal Type</label>
                <select
                  style={inputStyle}
                  value={form.mealType || ""}
                  onChange={(e) => set("mealType", e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">— Select —</option>
                  {["Breakfast", "Lunch", "Dinner", "Snacks", "Drinks"].map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 2 }}>
                <label style={labelStyle}>Guest Name</label>
                <input
                  style={{ ...inputStyle, marginBottom: 10 }}
                  value={form.guestName || ""}
                  onChange={(e) => set("guestName", e.target.value)}
                  placeholder="e.g. Jane Doe"
                  disabled={readOnly}
                />
                <label style={labelStyle}>Guest Affiliation</label>
                <input
                  style={inputStyle}
                  value={form.guestAffiliation || ""}
                  onChange={(e) => set("guestAffiliation", e.target.value)}
                  placeholder="e.g. Acme Corp, Client"
                  disabled={readOnly}
                />
              </div>
            </div>
          )}

          {form.category !== 'mileage' && (
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Subtotal</label>
                <input
                  style={lockedInput('subtotal')}
                  type="number"
                  step="0.01"
                  value={form.subtotal || ""}
                  onChange={(e) => setComponent("subtotal", e.target.value)}
                  placeholder="0.00"
                  disabled={readOnly || isLocked('subtotal')}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Tax</label>
                <input
                  style={lockedInput('tax')}
                  type="number"
                  step="0.01"
                  value={form.tax || ""}
                  onChange={(e) => setComponent("tax", e.target.value)}
                  placeholder="0.00"
                  disabled={readOnly || isLocked('tax')}
                />
              </div>
            </div>
          )}

          {form.category !== 'mileage' && (
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Tip</label>
                <input
                  style={lockedInput('tip')}
                  type="number"
                  step="0.01"
                  value={form.tip || ""}
                  onChange={(e) => setComponent("tip", e.target.value)}
                  placeholder="0.00"
                  disabled={readOnly || isLocked('tip')}
                />
              </div>
            </div>
          )}

          <div>
            <label style={labelStyle}>Total</label>
            <input
              style={{ ...lockedInput('total'), ...(hasMismatch ? { borderColor: "#EF4444", background: "#FEF2F2" } : {}) }}
              type="number"
              step="0.01"
              value={form.total || ""}
              onChange={(e) => set("total", e.target.value)}
              placeholder="0.00"
              disabled={readOnly || isLocked('total')}
            />
          </div>

          {hasMismatch && (
            <div style={{
              background: "#FEF2F2", border: "1.5px solid #FCA5A5", borderRadius: 10,
              padding: "10px 12px", fontSize: 12, color: "#DC2626", fontWeight: 600, marginTop: -8,
            }}>
              ⚠ Total ({form.total}) doesn't equal subtotal + tax + tip ({componentSum.toFixed(2)}). Please check the numbers.
            </div>
          )}

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Currency</label>
              <select
                style={lockedInput('currency')}
                value={form.currency || "USD"}
                onChange={(e) => set("currency", e.target.value)}
                disabled={readOnly || isLocked('currency')}
              >
                {CURRENCIES.map((cur) => (
                  <option key={cur.code} value={cur.code}>
                    {cur.code} ({cur.symbol})
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Date</label>
              <input
                style={lockedInput('date')}
                type="date"
                value={form.date || ""}
                onChange={(e) => set("date", e.target.value)}
                disabled={readOnly || isLocked('date')}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
             <div style={{ flex: 1 }}>
              <label style={labelStyle}>Start Date (opt)</label>
              <input
                style={inputStyle}
                type="date"
                value={form.start_date || null}
                onChange={(e) => set("start_date", e.target.value)}
                disabled={readOnly}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>End Date (opt)</label>
              <input
                style={inputStyle}
                type="date"
                value={form.end_date || null }
                onChange={(e) => set("end_date", e.target.value)}
                disabled={readOnly}
              />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Notes</label>
            <textarea
              style={{
                ...inputStyle,
                minHeight: 80,
                resize: "vertical",
              }}
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Add any notes..."
              disabled={readOnly}
            />
          </div>

          {/* Reimbursed toggle — business expenses only */}
          {cls === 'business_expense' && !readOnly && (
            <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 14, marginTop: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!form.reimbursed}
                  onChange={e => set('reimbursed', e.target.checked || undefined)}
                  style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#0A7C8A' }}
                />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Reimbursed</span>
                {form.reimbursed && (
                  <span style={{ fontSize: 11, color: '#6B7280' }}>— excluded from outstanding balance</span>
                )}
              </label>
            </div>
          )}

          {/* Copy / Move to another report */}
          {canTransfer && (
            <div style={{
              borderTop: "1px solid #E5E7EB",
              paddingTop: 14,
              marginTop: 4,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showTransfer ? 10 : 0 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Transfer to another report
                </span>
                <button type="button" onClick={() => setShowTransfer(v => !v)} style={linkBtnStyle}>
                  {showTransfer ? "Hide" : "Show"}
                </button>
              </div>

              {showTransfer && (
                <div>
                  <select
                    value={transferTarget || otherFiles[0]?.name || ''}
                    onChange={e => setTransferTarget(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 10px", borderRadius: 8,
                      border: "1.5px solid #E5E7EB", fontSize: 13, color: "#374151",
                      background: "#fff", outline: "none", marginBottom: 10,
                      boxSizing: "border-box",
                    }}
                  >
                    {otherFiles.map(f => (
                      <option key={f.name} value={f.name}>{f.name}</option>
                    ))}
                  </select>

                  {transferDone ? (
                    <div style={{
                      textAlign: "center", padding: "10px", borderRadius: 8,
                      background: "#f0fdf4", color: "#16a34a", fontSize: 13, fontWeight: 700,
                    }}>
                      {transferDone === 'copy' ? '✓ Copied!' : '✓ Moved!'}
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 8 }}>
                      {onCopyExpense && (
                        <button
                          type="button"
                          onClick={() => handleTransfer('copy')}
                          disabled={!!transferring}
                          style={{
                            flex: 1, padding: "10px", borderRadius: 9,
                            border: "1.5px solid #0A7C8A", background: "#fff",
                            color: "#0A7C8A", fontSize: 13, fontWeight: 700,
                            cursor: transferring ? "not-allowed" : "pointer",
                            opacity: transferring ? 0.6 : 1,
                          }}
                        >
                          {transferring === 'copy' ? 'Copying…' : 'Copy'}
                        </button>
                      )}
                      {onMoveExpense && (
                        <button
                          type="button"
                          onClick={() => handleTransfer('move')}
                          disabled={!!transferring}
                          style={{
                            flex: 1, padding: "10px", borderRadius: 9,
                            border: "none",
                            background: transferring ? "#9ca3af" : "linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)",
                            color: "#fff", fontSize: 13, fontWeight: 700,
                            cursor: transferring ? "not-allowed" : "pointer",
                          }}
                        >
                          {transferring === 'move' ? 'Moving…' : 'Move'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {readOnly ? (
            <button
              onClick={onClose}
              style={{
                width: "100%",
                marginTop: 8,
                padding: "14px",
                borderRadius: 12,
                border: "1.5px solid #E5E7EB",
                background: "#fff",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                color: "#6B7280",
              }}
            >
              Close
            </button>
          ) : (
            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: "14px",
                  borderRadius: 12,
                  border: "1.5px solid #E5E7EB",
                  background: "#fff",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  color: "#6B7280",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => onSave(form)}
                style={{
                  flex: 2,
                  padding: "14px",
                  borderRadius: 12,
                  border: "none",
                  background: "linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  color: "#fff",
                  boxShadow: "0 4px 15px rgba(5, 196, 176, 0.3)",
                }}
              >
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const linkBtnStyle = {
  background: "none",
  border: "none",
  color: "#0A7C8A",
  fontSize: 11,
  fontWeight: 700,
  cursor: "pointer",
  padding: 0,
};

const newCatBoxStyle = {
  background: "#f5f3ff",
  border: "1.5px solid #c4b5fd",
  borderRadius: 10,
  padding: "12px",
};

const addBtnStyle = {
  width: "100%",
  padding: "8px",
  borderRadius: 8,
  border: "none",
  background: "linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)",
  color: "#fff",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
};

export default EditModal;
